import { getPrimaryColor } from '@/configs/theme.ts';

import { FC, useCallback, useContext } from 'react';

import { Flex, Select, Stack, Text } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';

import { AppContext } from '@/contexts/AppContext/AppContextProvider.tsx';

import { CarPassSelector } from '@/entities/CarPassSelector/CarPassSelector.tsx';
import { useUpdatePersonSettings } from '@/entities/Settings';

import { stringifyObjectValues } from '@/shared/helpers/utils.ts';
import useAppStore from '@/shared/store/app.store.ts';
import { getSettings, useSettingsStore } from '@/shared/store/settings.store.ts';
import useUserStore from '@/shared/store/user.store.ts';
import { normalizeWorkTime } from '@/shared/helpers/workTime.ts';
import { ISettings, TDateRange } from '@/shared/types/app.types.ts';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { PushNotificationButton } from '@/shared/uikit/components/button/PushNotificationButton';
import { RangeTimePicker } from '@/shared/uikit/components/picker/RangeTimePicker';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

type Props = {
	isOpen?: boolean;
	onClose?: () => void;
};

const Settings: FC<Props> = ({ isOpen, onClose }) => {
	const isMobile = useAppStore((state) => state.isMobile);
	const id = useSettingsStore((state) => state.id);
	const userCarPasses = useUserStore((state) => state.carPasses);
	const defaultWorkTime = useSettingsStore((state) => state.workTime);
	const defaultCarNumber = useSettingsStore((state) => state.carNumber);
	const setWorkTime = useSettingsStore((state) => state.setWorkTime);
	const setDefaultCarNumber = useSettingsStore((state) => state.setCarNumber);
	const { admin } = useContext(AppContext);
	const [theme, setTheme] = useLocalStorage<string | null>({ key: 'theme' });

	const { updateSettings, isUpdatingSettings, isUpdatingSettingsError } = useUpdatePersonSettings();

	const ThemeSelect = useCallback(
		() => (
			<Select
				defaultValue={theme}
				onChange={setTheme}
				data={[{ label: 'по-умолчанию', value: getPrimaryColor() }].concat(
					[
						{ label: 'зеленый', value: 'phs' },
						{ label: 'красный', value: 'lab' },
						{ label: 'серый', value: 'fsoft' },
					].filter(({ value }) => value !== getPrimaryColor()),
				)}
				allowDeselect={false}
			/>
		),
		[theme],
	);

	const handleSetDefaultWorkTime = (workTime: TDateRange) => {
		setWorkTime(workTime);
	};

	const handleSetDefaultCarNumber = (carNumber: ISettings['carNumber']) => {
		const settings = getSettings();
		const currentCarNumber = userCarPasses.find((cp) => cp.value === carNumber);

		if (currentCarNumber) {
			const newSettings: { [key: string]: unknown } = {
				...settings,
				carNumber: currentCarNumber,
			};

			stringifyObjectValues(newSettings);

			updateSettings({ settingsId: String(id), payload: { ...newSettings } });
			setDefaultCarNumber(currentCarNumber);
		}
	};

	const items = [
		{
			label: 'Рабочее время',
			description: 'Время, используемое по умолчанию при бронировании',
			input: (
				<RangeTimePicker
					isTaxi={false}
					value={normalizeWorkTime(defaultWorkTime)}
					onChange={handleSetDefaultWorkTime}
					isDefault
				/>
			),
			isAdmin: false,
		},
		{
			label: 'Номер автомобиля',
			description: 'Номер, используемый по умолчанию при бронировании',
			input: (
				<CarPassSelector
					carPasses={userCarPasses}
					value={defaultCarNumber?.value ?? ''}
					onChange={(value) => handleSetDefaultCarNumber(value as ISettings['carNumber'])}
					onAutoSelect={(value) =>
						setDefaultCarNumber(userCarPasses.find((cp) => cp.value === value) ?? null)
					}
				/>
			),
			isAdmin: false,
		},
		{
			label: 'Тема системы',
			description: '',
			input: <ThemeSelect />,
			isAdmin: true,
		},
		{
			label: 'Включить push-уведомления',
			description: '',
			input: <PushNotificationButton />,
			isAdmin: false,
		},
	];

	const content = (
		<Flex direction="column" gap="md">
			{items?.map((item) => {
				if (!admin && item.isAdmin) return;

				return (
					<Flex
						key={item.label}
						align="center"
						justify={isMobile ? 'space-between' : 'start'}
						gap="md"
					>
						<Stack w={isMobile ? '50%' : '25%'} gap={0}>
							<Text fz={16} fw={600} lh="20px">
								{item.label}
							</Text>
							<Text c="dimmed" fz={14} fw={500} lh="18px">
								{item.description}
							</Text>
						</Stack>
						<Flex w={isMobile ? '50%' : '25%'} direction="column">
							{item.input}
						</Flex>
					</Flex>
				);
			})}
		</Flex>
	);

	if (isMobile) {
		return (
			<BottomSheet
				opened={isOpen!}
				onClose={onClose!}
				title="Настройки"
				withCloseButton={false}
				isFullScreen
			>
				{content}
			</BottomSheet>
		);
	}

	return (
		<LayoutRow
			title={{
				title: { children: 'Настройки', size: 'h4', fw: 800 },
			}}
			type="once"
			style={{ height: 'calc(100dvh - 116px)' }}
		>
			{content}
		</LayoutRow>
	);
};

export default Settings;
